import { useState, useMemo } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSliders, faEllipsis } from "@fortawesome/free-solid-svg-icons";
import states from "states-us";
import FosterFilterDrawer from "../FosterFilterDrawer";
import DataTable from "../../../shared/DataTable/DataTable";

function convertState(abbr) {
  const found = states.find((s) => s.abbreviation === abbr);
  return found ? found.name : abbr;
}

export const FosterTable = ({ data, serverPagination, serverPageCount, serverTotalCount, onServerPageChange }) => {
  const [selectedIds, setSelectedIds] = useState([]);
  const [openMenuId, setOpenMenuId] = useState(null);
  const [filterOpen, setFilterOpen] = useState(false);
  const [appliedFilters, setAppliedFilters] = useState({ cities: [], state: "" });

  const uniqueCities = useMemo(
    () => [...new Set(data.map((f) => f.city).filter(Boolean))].sort(),
    [data]
  );

  const filteredData = useMemo(() => {
    const { cities = [], state = "" } = appliedFilters;
    return data.filter((f) => {
      const matchesCity = cities.length === 0 || cities.includes(f.city);
      const matchesState = !state || f.state === state;
      return matchesCity && matchesState;
    });
  }, [data, appliedFilters]);

  const columns = useMemo(() => [
    {
      id: "select",
      header: () => null,
      cell: ({ row }) => {
        const id = row.original.id ?? row.index;
        return (
          <input
            type="checkbox"
            checked={selectedIds.includes(id)}
            onChange={() =>
              setSelectedIds((prev) =>
                prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
              )
            }
            className="w-4 h-4 rounded border-gray-300 accent-blue cursor-pointer"
          />
        );
      },
      enableSorting: false,
      enableGlobalFilter: false,
      size: 40,
    },
    {
      accessorKey: "full_name",
      header: "Name",
    },
    {
      accessorKey: "city",
      header: "City",
    },
    {
      accessorKey: "state",
      header: "State",
      cell: ({ getValue }) => convertState(getValue()),
    },
    {
      accessorKey: "country",
      header: "Country",
    },
    {
      id: "actions",
      header: () => null,
      cell: ({ row }) => {
        const id = row.original.id ?? row.index;
        return (
          <div className="text-center relative">
            <button
              onClick={() => setOpenMenuId(openMenuId === id ? null : id)}
              className="text-gray-500 hover:text-gray-800 bg-transparent border-none outline-none"
            >
              <FontAwesomeIcon icon={faEllipsis} />
            </button>
          </div>
        );
      },
      enableSorting: false,
      enableGlobalFilter: false,
      size: 40,
    },
  ], [selectedIds, openMenuId]);

  const filterSlot = (
    <>
      <button
        onClick={() => setFilterOpen(true)}
        className="flex items-center gap-2 border border-gray-300 rounded-full px-4 py-2 text-sm text-gray-700 bg-white hover:bg-gray-50 whitespace-nowrap"
      >
        <FontAwesomeIcon icon={faSliders} size="sm" />
        Filter
      </button>
      <FosterFilterDrawer
        isOpen={filterOpen}
        onClose={() => setFilterOpen(false)}
        cities={uniqueCities}
        appliedFilters={appliedFilters}
        onApply={(filters) => setAppliedFilters(filters)}
      />
    </>
  );

  return (
    <DataTable
      columns={columns}
      data={filteredData}
      filterPlaceholder="Search by name, city, state, or country"
      filterSlot={filterSlot}
      totalLabel="Total Fosters"
      emptyMessage="No fosters found."
      serverPagination={serverPagination}
      serverPageCount={serverPageCount}
      serverTotalCount={serverTotalCount}
      onServerPageChange={onServerPageChange}
    />
  );
};
